import { useQuery } from '@apollo/client';
import { Button, Flex, Grid, IconButton, Select, Spinner, Stack, Text } from '@chakra-ui/react';
import { Equals, List as ListIcon, SortAscending, SortDescending, SquaresFour } from '@phosphor-icons/react';
import { RouteComponentProps } from '@reach/router';
import { useEffect, useState } from 'react';

import Layout from '../../../components/Layout';
import { HubCounterCard } from './Card';
import { HUB_COUNTER_SORT_OPTIONS, HubCounterSortField, localStatusToGraphqlStatus } from './listStatus.utils';
import { GET_HUB_COUNTERS, HubCountersQuery } from './query';
import { HubCounterTableRow } from './Row';

interface HubCountersListProps extends RouteComponentProps {}

const getUrlParams = () => new URLSearchParams(window.location.search);

export const HubCountersList: React.FC<HubCountersListProps> = () => {
  const params = getUrlParams();
  const [status, setStatus] = useState(params.get('status') || 'ALL');
  const [loginRequired, setLoginRequired] = useState(params.get('loginRequired') === '1');
  const [sortBy, setSortBy] = useState<HubCounterSortField>((params.get('sortBy') as HubCounterSortField) || 'hubCounterID');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>((params.get('sortOrder') as 'asc' | 'desc') || 'desc');
  const [view, setView] = useState<'card' | 'table'>((localStorage.getItem('hubCounterView') as 'card' | 'table') || 'card');

  const { data, loading, error } = useQuery<HubCountersQuery>(GET_HUB_COUNTERS, {
    variables: {
      status: localStatusToGraphqlStatus(status),
      sortBy,
      sortOrder,
      loginRequired: loginRequired || undefined,
    },
  });

  useEffect(() => {
    const nextParams = new URLSearchParams();
    if (status !== 'ALL') nextParams.set('status', status);
    if (loginRequired) nextParams.set('loginRequired', '1');
    if (sortBy !== 'hubCounterID') nextParams.set('sortBy', sortBy);
    if (sortOrder !== 'desc') nextParams.set('sortOrder', sortOrder);
    const newUrl = `${window.location.pathname}${nextParams.toString() ? '?' + nextParams.toString() : ''}`;
    window.history.replaceState({}, '', newUrl);
  }, [status, loginRequired, sortBy, sortOrder]);

  const switchView = (type: 'card' | 'table') => {
    localStorage.setItem('hubCounterView', type);
    setView(type);
  };

  const HubCounterComponent = view === 'card' ? HubCounterCard : HubCounterTableRow;

  return (
    <Layout>
      <Flex flexDirection={['column', 'row']} alignItems="center" justifyContent="space-between" mb="6">
        <Text as="h2" fontSize={['2xl', '4xl']} fontWeight="bold" flex="1" fontFamily="heading">
          Hub Counters
        </Text>
      </Flex>

      <Flex justifyContent="space-between" alignItems="flex-start" mb="4" flexWrap="wrap" gap="3">
        <Stack>
          <Text fontSize="sm" gridColumn="span 3">
            Angezeigte Hub Counter: {data?.hubCounters?.length}
          </Text>
        </Stack>

        <Flex flexWrap="wrap" justifyContent="flex-end" alignItems="center" flex="1" gap="4">
          <Flex flexWrap="wrap" justifyContent="flex-end" gap="2">
            <Button size="sm" onClick={() => setStatus('ALL')} colorScheme={status === 'ALL' ? 'blue' : undefined}>
              Alle
            </Button>
            <Button size="sm" onClick={() => setStatus('DRAFT')} colorScheme={status === 'DRAFT' ? 'blue' : undefined}>
              Draft
            </Button>
            <Button size="sm" onClick={() => setStatus('PENDING')} colorScheme={status === 'PENDING' ? 'blue' : undefined}>
              Pending
            </Button>
            <Button size="sm" onClick={() => setStatus('WAITLIST')} colorScheme={status === 'WAITLIST' ? 'blue' : undefined}>
              Auf Warteliste
            </Button>
            <Button size="sm" onClick={() => setStatus('CONFIRMED')} colorScheme={status === 'CONFIRMED' ? 'green' : undefined}>
              Genehmigt
            </Button>
            <Button size="sm" onClick={() => setStatus('REJECTED')} colorScheme={status === 'REJECTED' ? 'red' : undefined}>
              Nicht Freigegeben
            </Button>
            <Button size="sm" onClick={() => setLoginRequired((value) => !value)} colorScheme={loginRequired ? 'yellow' : undefined}>
              Login erforderlich
            </Button>
          </Flex>
          <Flex flexWrap="wrap" justifyContent="flex-end" gap="2" alignItems="center">
            <Select
              size="sm"
              w="220px"
              rounded="md"
              value={sortBy}
              onChange={(event) => setSortBy(event.target.value as HubCounterSortField)}
            >
              {HUB_COUNTER_SORT_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Select>
            <IconButton
              variant="ghost"
              size="sm"
              aria-label={sortOrder === 'asc' ? 'Absteigend sortieren' : 'Aufsteigend sortieren'}
              icon={sortOrder === 'asc' ? <SortAscending /> : sortOrder === 'desc' ? <SortDescending /> : <Equals />}
              onClick={() => setSortOrder((current) => (current === 'asc' ? 'desc' : 'asc'))}
            />
            <IconButton
              variant="ghost"
              size="sm"
              aria-label={view === 'card' ? 'Switch to Table' : 'Switch to Card'}
              icon={view === 'card' ? <ListIcon /> : <SquaresFour />}
              onClick={() => switchView(view === 'card' ? 'table' : 'card')}
            />
          </Flex>
        </Flex>
      </Flex>

      {error ? (
        <Flex width="100%" height="100%" justifyContent="center" alignItems="center">
          Ein Fehler ist aufgetreten. Schreib Osamah an.
        </Flex>
      ) : null}
      {data && data.hubCounters ? (
        <Grid gridTemplateColumns={['1fr', '1fr 1fr', '1fr 1fr', '1fr 1fr']} gap={view === 'card' ? '16px' : 0} mb="8">
          {data.hubCounters.length === 0 ? (
            <Text gridColumn="span 3" p="4" textAlign="center" backgroundColor="red.100" color="red.800" borderRadius="4px">
              Der Widerstand versteckt sich gut und konnte nicht gefunden werden. Wiederhole die Suche oder schicke Boba Fett!
            </Text>
          ) : (
            data.hubCounters.map((item) => {
              if (!item?.hubCounter) return null;
              return <HubCounterComponent key={item.hubCounter.id} {...item} />;
            })
          )}
        </Grid>
      ) : null}

      {loading ? (
        <Flex width="100%" height="100%" justifyContent="center" alignItems="center">
          <Spinner />
        </Flex>
      ) : null}
    </Layout>
  );
};
